import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Button,
  Paper,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Alert,
  AlertTitle,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Chip,
  Container,
  Stack,
  useMediaQuery,
  Snackbar,
  Skeleton,
  Grid,
  MenuItem,
  Avatar,
  LinearProgress,
  Fab,
  SwipeableDrawer,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  BottomNavigation,
  BottomNavigationAction
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import LogoutIcon from "@mui/icons-material/Logout";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import StarIcon from "@mui/icons-material/Star";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaletteIcon from "@mui/icons-material/Palette";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import API from "../../../API";
import AlertDialog from "../../../Components/AlertDialog";
import useAlertDialog from "../../../Components/useAlertDialog";
import { replaceConsoleError } from '../../../Utils/toastNotifications';
import ConsentForm from "../popups/consentform";
import UploadFileDialog from "../../../Components/tools/uploadfile";
import { useCreatorAuth } from "../../../Hooks/creator-use-auth";
import routes from "../../../Config/routes";
import PlatformsTab from "./editCreator/PlatformsTab";
import PartnerTab from "./editCreator/PartnerTab";
import NotifSettings from "../../../Pages/Dashboard/Users/notif_settings";
import WalletCard from "./editCreator/WalletCard";
import WalletDialog from "./editCreator/WalletDialog";
import TaxInfoDialog from "./editCreator/TaxInfoDialog";
import PaymentPreferencesCard from "./editCreator/PaymentPreferencesCard";
import { useWalletManagement } from './editCreator/useWalletManagement';
import { useTaxManagement } from './editCreator/useTaxManagement';
import { TAX_ID_TYPES } from './editCreator/constants';
import { SketchPicker } from "react-color";
import { FormHelperText } from '@mui/material';
import CreatorContractView from "../../../Pages/Contracts/creatorcontractview";

const APPSHELL_BAR_HEIGHT = 86;
const EDITCREATOR_TAB_BAR_HEIGHT = 70;
const MOBILE_BOTTOM_SPACING = APPSHELL_BAR_HEIGHT + EDITCREATOR_TAB_BAR_HEIGHT + 16;

export default function EditCreator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get("tab") || "platforms";
  const navigate = useNavigate();
  const { logout } = useCreatorAuth();
  const contentRef = useRef(null);
  const initialValuesRef = useRef(null);
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [completionProgress, setCompletionProgress] = useState(0);
  const { dialogState } = useAlertDialog();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  
  const [creatorName, setCreatorName] = useState("");
  const [creatorRegion, setCreatorRegion] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [race, setRace] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [primaryMarket, setPrimaryMarket] = useState([]);
  const [notesContentStyle, setNotesContentStyle] = useState("");
  const [profilePhotoUrl, setProfilePhotoUrl] = useState("");
  const [isVendor, setIsVendor] = useState(false);
  const [forwardedAlias, setForwardedAlias] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("PayPal");
  const [paymentEmail, setPaymentEmail] = useState("");
  const [processEmails, setProcessEmails] = useState(true);
  const [refreshToken, setRefreshToken] = useState("");
  
  // NEW: Platform summary data from child component
  const [platformSummary, setPlatformSummary] = useState({
    totalFollowers: 0,
    activePlatforms: 0,
    platformData: {}
  });
  
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);
  
  const {
    wallets,
    walletsLoading,
    walletDialog,
    newWallet,
    fetchWallets,
    handleWalletChange,
    handleDeleteWallet,
    handleSetPrimaryWallet,
    handleOpenWalletDialog,
    handleCloseWalletDialog,
    handleSubmitWallet,
  } = useWalletManagement(showSnackbar);
  
  const {
    taxInfo,
    taxInfoLoading,
    taxDialog,
    newTaxInfo,
    fetchTaxInfo,
    handleTaxChange,
    handleSaveTaxInfo,
    handleOpenTaxDialog,
    handleCloseTaxDialog,
  } = useTaxManagement(showSnackbar);
  
  const { data: accountStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery(
    ['creator-account-status'],
    () => API.accountLinking.getStatus(),
    {
      staleTime: 1000 * 60 * 5,
      onError: (error) => replaceConsoleError('Failed to fetch account status', error)
    }
  );
  
  const switchContextMutation = useMutation(
    (contextType) => API.accountLinking.switchContext(contextType),
    {
      onSuccess: (result) => {
        if (result.requires_new_token) {
          window.location.href = result.new_context === 'user' ? '/dashboard' : '/creatorconnect/start';
        } else {
          refetchStatus();
        }
      },
      onError: (error) => {
        replaceConsoleError('Failed to switch context', error);
        showSnackbar(error?.response?.data?.error || "Failed to switch account context", "error");
      }
    }
  );
  
  const [appearanceSettings, setAppearanceSettings] = useState({
    darkMode: null,
    fontPreference: "default",
    uiDensity: "normal",
    themeColors: {
      primary: theme.palette.mode === 'dark' ? "#cb534f" : "#7b2a2a",
      secondary: "#646aff",
    },
  });
  
  const { data: userSettings, refetch: refetchSettings } = useQuery(
    ["creatorSettings"],
    () => API.users.fetchUserSettings(),
    {
      onSuccess: (data) => {
        if (data?.id) {
          setAppearanceSettings({
            darkMode: data.dark_mode,
            fontPreference: data.font_preference,
            uiDensity: data.ui_density,
            themeColors: data.theme_colors || {
              primary: theme.palette.mode === 'dark' ? "#cb534f" : "#7b2a2a",
              secondary: "#646aff",
            },
          });
        }
      },
      onError: (error) => {
        if (error?.response?.status !== 404) {
          replaceConsoleError('Failed to fetch creator appearance settings', error);
        }
      }
    }
  );
  
  const saveAppearanceSettingsMutation = useMutation(
    (payload) =>
      userSettings?.id
        ? API.users.updateUserSettings({ id: userSettings.id, ...payload })
        : API.users.createUserSettings(payload),
    {
      onSuccess: () => {
        refetchSettings();
        showSnackbar("Appearance settings saved successfully!");
      },
      onError: (error) => {
        showSnackbar(error?.response?.data?.error || "Failed to save appearance settings", "error");
      },
    }
  );
  
  const handleSaveAppearance = useCallback(async () => {
    await saveAppearanceSettingsMutation.mutateAsync({
      dark_mode: appearanceSettings.darkMode,
      font_preference: appearanceSettings.fontPreference,
      ui_density: appearanceSettings.uiDensity,
      theme_colors: appearanceSettings.themeColors,
    });
  }, [appearanceSettings, saveAppearanceSettingsMutation]);
  
  const handleLogout = useCallback(() => {
    logout();
    return window.location.href = '/';
  }, [logout, navigate]);
  
  const handleMenuOpen = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);
  const handleSwitchContext = (contextType) => switchContextMutation.mutate(contextType);
  const navigateToAccountManager = () => navigate('/global-linking');
  
  // Callback to receive platform data from PlatformsTab
  const handlePlatformDataUpdate = useCallback((data) => {
    setPlatformSummary(data);
  }, []);
  
  const calculateProgress = useCallback(() => {
    let completed = 0;
    let total = 0;
    
    // Platform completion (33%)
    if (platformSummary.activePlatforms > 0) completed += 1;
    total += 1;
    
    // Wallet completion (33%)
    if (wallets.length > 0) completed += 1;
    total += 1;
    
    // Tax info completion (33%)
    if (taxInfo) completed += 1;
    total += 1;
    
    return Math.round((completed / total) * 100);
  }, [platformSummary.activePlatforms, wallets, taxInfo]);
  
  useEffect(() => {
    setCompletionProgress(calculateProgress());
  }, [calculateProgress]);
  
  const GetUserData = useCallback(async () => {
    try {
      setIsLoading(true);
      const creatorRes = await API.creatorConnect.getCreator();
      const user = creatorRes.user;
      const cr = user.creator;
      
      setCreatorName(cr.display_name || "");
      setCreatorRegion(cr.region || "");
      setContactEmail(cr.email);
      setPhoneNumber(cr.phone_number || "1");
      setRace(cr.ethnicity || "");
      setGender(cr.gender || "");
      setLocation(cr.geolocation || "");
      setPrimaryMarket(cr.primary_markets || []);
      setNotesContentStyle(cr.notes_content_style || "");
      setProfilePhotoUrl(cr.pfphref || "");
      setForwardedAlias(user?.forwarded_alias ?? "");
      setIsVendor(!!cr.is_vendor);
      setPaymentEmail(user.paypal || "");
      setPaymentMethod(user.payout_preferred || "");
      setProcessEmails(user.process_emails);
      setRefreshToken(user.refresh_token || "");
      
      await Promise.all([fetchWallets(), fetchTaxInfo()]);
      
      initialValuesRef.current = {
        creatorName: cr.display_name || "",
        processEmails: user.process_emails,
        forwardedAlias: user?.forwarded_alias ?? "",
        paymentEmail: user.paypal || "",
        paymentMethod: user.payout_preferred || "",
        profilePhotoUrl: cr.pfphref || "",
        phoneNumber: cr.phone_number || "1",
        location: cr.geolocation || "",
        gender: cr.gender || "",
        race: cr.ethnicity || "",
        creatorRegion: cr.region || "",
        primaryMarket: cr.primary_markets || [],
        notesContentStyle: cr.notes_content_style || "",
      };
    } catch (err) {
      showSnackbar(err?.response?.data?.error || err.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [fetchWallets, fetchTaxInfo, showSnackbar]);
  
  useEffect(() => {
    GetUserData();
  }, [GetUserData]);
  
  const hasUnsavedChanges = useMemo(() => {
    if (!initialValuesRef.current) return false;
    const initial = initialValuesRef.current;
    return (
      creatorName !== initial.creatorName ||
      processEmails !== initial.processEmails ||
      forwardedAlias !== initial.forwardedAlias ||
      paymentEmail !== initial.paymentEmail ||
      paymentMethod !== initial.paymentMethod ||
      profilePhotoUrl !== initial.profilePhotoUrl ||
      phoneNumber !== initial.phoneNumber ||
      location !== initial.location ||
      gender !== initial.gender ||
      race !== initial.race ||
      creatorRegion !== initial.creatorRegion ||
      JSON.stringify(primaryMarket) !== JSON.stringify(initial.primaryMarket) ||
      notesContentStyle !== initial.notesContentStyle
    );
  }, [
    creatorName, processEmails, forwardedAlias, paymentEmail, paymentMethod,
    profilePhotoUrl, phoneNumber, location, gender, race, creatorRegion,
    primaryMarket, notesContentStyle
  ]);
  
  const handleSaveNonPlatformData = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        geolocation: location,
        gender,
        ethnicity: race,
        primary_markets: primaryMarket,
        region: creatorRegion,
        notescontent_style: notesContentStyle || null,
        contactEmail: contactEmail || null,
        paymentEmail: paymentEmail || null,
        phone_number: phoneNumber || null,
        payment_method: paymentMethod || null,
        pfphref: profilePhotoUrl,
        process_emails: processEmails,
        display_name: creatorName,
        is_vendor: isVendor,
        creator_type: isVendor ? "vendor" : "creator",
        forwarded_alias: forwardedAlias || null,
      };
      
      const creatorResponse = await API.creatorConnect.editCreator(payload);
      const updatedFields = creatorResponse?.updated_fields || [];
      const currentStatus = creatorResponse?.current_status || {};
      
      let message = "Profile saved successfully!";
      if (updatedFields.length > 0) {
        const fieldNames = updatedFields.map(field => {
          if (field === 'process_emails') return 'Email automation';
          if (field === 'forwarded_alias') return 'Forwarding address';
          if (field === 'profile_picture') return 'Profile photo';
          return field;
        }).join(', ');
        message = `Updated: ${fieldNames}`;
      }
      
      showSnackbar(message);
      
      if (initialValuesRef.current) {
        initialValuesRef.current = {
          creatorName: payload.display_name || "",
          processEmails: currentStatus.process_emails ?? payload.process_emails,
          forwardedAlias: (currentStatus.forwarded_alias ?? payload.forwarded_alias) || "",
          paymentEmail: payload.paymentEmail || "",
          paymentMethod: payload.payment_method || "",
          profilePhotoUrl: payload.pfphref || "",
          phoneNumber: payload.phone_number || "1",
          location: payload.geolocation || "",
          gender: payload.gender || "",
          race: payload.ethnicity || "",
          creatorRegion: payload.region || "",
          primaryMarket: payload.primary_markets || [],
          notesContentStyle: payload.notescontent_style || "",
        };
      }
      
      await GetUserData();
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.details || err.message;
      showSnackbar(errorMsg, "error");
    } finally {
      setIsLoading(false);
    }
  }, [location, gender, race, primaryMarket, creatorRegion, notesContentStyle,
      contactEmail, paymentEmail, phoneNumber, paymentMethod, profilePhotoUrl,
      processEmails, creatorName, isVendor, forwardedAlias, showSnackbar, GetUserData]);
  
  const handleChangeTab = useCallback((_, v) => {
    setSearchParams(prev => ({ ...Object.fromEntries(prev), tab: v }));
    setMobileMenuOpen(false);
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [setSearchParams]);
  
  const tabItems = [
    { value: "platforms", label: "Platforms", icon: MonetizationOnIcon, error: 0 },
    { value: "partner", label: "Partner", icon: BusinessIcon, error: 0 },
    { value: "wallets", label: "Payments", icon: AccountBalanceWalletIcon, error: wallets.length === 0 ? 1 : 0 },
    { value: "appearance", label: "Appearance", icon: PaletteIcon, error: 0 },
    { value: "notifications", label: "Notifications", icon: NotificationsIcon, error: 0 },
    { value: "contracts", label: "Contracts", icon: MonetizationOnIcon, error: 0 },
    { value: "accounts", icon: AccountTreeIcon, label: "Account Manager", primary: true },
  ];
  
  const status = accountStatus?.data;
  const hasUserAccount = status?.has_user_account;
  const hasCreatorAccount = status?.has_creator_account;
  const currentContext = status?.current_context;
  
  const MobileNavigation = () => (
    <SwipeableDrawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      onOpen={() => setMobileMenuOpen(true)}
      PaperProps={{ sx: { width: 280, backgroundColor: "background.paper" } }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={600}>Navigation</Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}><CloseIcon /></IconButton>
        </Box>
      </Box>
      <List sx={{ p: 0 }}>
        {tabItems.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <ListItemButton
              key={tab.value}
              selected={tabValue === tab.value}
              onClick={() => handleChangeTab(null, tab.value)}
              sx={{
                py: 2, px: 3,
                "&.Mui-selected": {
                  backgroundColor: tab.primary ? "primary.main" : "primary.50",
                  borderRight: 3,
                  borderColor: "primary.main",
                  color: tab.primary ? "white" : "primary.main",
                }
              }}
            >
              <ListItemIcon>
                <Badge variant="dot" color="error" invisible={tab.error === 0}>
                  <IconComponent color={tabValue === tab.value ? (tab.primary ? "inherit" : "primary") : "inherit"} />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary={tab.label}
                primaryTypographyProps={{
                  fontWeight: tabValue === tab.value ? 600 : 400,
                  color: tabValue === tab.value && tab.primary ? "inherit" : undefined
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </SwipeableDrawer>
  );
  
  const AccountManagerTab = () => (
    <Box>
      <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}>
        Account Manager
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage and switch between your linked accounts.
      </Typography>
      {statusLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwitchAccountIcon />Current Account Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{
                  height: '100%',
                  border: hasCreatorAccount ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                  backgroundColor: hasCreatorAccount ? '#f3e5f5' : '#fafafa'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <StarIcon color={hasCreatorAccount ? 'secondary' : 'disabled'} />
                      <Typography variant="h6">Creator Account</Typography>
                      {hasCreatorAccount && <Chip label="Linked" sx={{ backgroundColor: '#9c27b0', color: 'white' }} size="small" />}
                    </Box>
                    {hasCreatorAccount ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Handle:</strong> @{status.creator_info?.username}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Display Name:</strong> {status.creator_info?.display_name}
                        </Typography>
                        {currentContext === 'creator' && (
                          <Chip label="Currently Active" sx={{ backgroundColor: '#9c27b0', color: 'white' }} size="small" />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No creator account linked</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{
                  height: '100%',
                  border: hasUserAccount ? '2px solid #4caf50' : '1px solid #e0e0e0',
                  backgroundColor: hasUserAccount ? '#f1f8e9' : '#fafafa'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <BusinessIcon color={hasUserAccount ? 'success' : 'disabled'} />
                      <Typography variant="h6">Business Account</Typography>
                      {hasUserAccount && <Chip label="Linked" color="success" size="small" />}
                    </Box>
                    {hasUserAccount ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Name:</strong> {status.user_info?.first_name} {status.user_info?.last_name}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Company:</strong> {status.user_info?.company_name}
                        </Typography>
                        {currentContext === 'normal' && (
                          <Chip label="Currently Active" color="primary" size="small" />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No business account linked</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon />Account Actions
            </Typography>
            <Grid container spacing={2}>
              {hasUserAccount && hasCreatorAccount && (
                <>
                  {currentContext !== 'creator' && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        startIcon={<StarIcon />}
                        onClick={() => handleSwitchContext('creator')}
                        disabled={switchContextMutation.isLoading}
                        size="large"
                      >
                        Switch to Creator
                      </Button>
                    </Grid>
                  )}
                  {currentContext !== 'normal' && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<BusinessIcon />}
                        onClick={() => handleSwitchContext('normal')}
                        disabled={switchContextMutation.isLoading}
                        size="large"
                      >
                        Switch to Business
                      </Button>
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AccountTreeIcon />}
                  onClick={navigateToAccountManager}
                  size="large"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': { borderColor: 'primary.dark', backgroundColor: 'primary.50' }
                  }}
                >
                  Full Account Manager
                </Button>
              </Grid>
            </Grid>
            {!hasUserAccount && !hasCreatorAccount && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You don't have any linked accounts yet. Use the Full Account Manager to create and link your accounts.
              </Alert>
            )}
            {(hasUserAccount || hasCreatorAccount) && !(hasUserAccount && hasCreatorAccount) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Link both business and creator accounts to enable seamless switching between contexts.
              </Alert>
            )}
            {switchContextMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {switchContextMutation.error?.response?.data?.error || 'Failed to switch account context'}
              </Alert>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
  
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <AlertDialog alertState={dialogState} />
      <ConsentForm open={false} onClose={() => {}} />
      
      <Box  sx={{
        mb: 3,
        borderRadius: isMobile ? 0 : 3,
        overflow: "hidden",
        // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "black",
      }}>
        {isMobile && (
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <IconButton color="inherit" onClick={() => setMobileMenuOpen(true)} sx={{ mr: 2 }}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                {creatorName || "Creator Settings"}
              </Typography>
              <IconButton color="inherit" onClick={handleMenuOpen}>
                <SettingsIcon />
              </IconButton>
            </Box>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => setIsUploadDialogOpen(true)}>
                <ListItemIcon><EditIcon /></ListItemIcon>
                <ListItemText>Change Photo</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon /></ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
        
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 2 : 3,
            mb: 3,
            flexDirection: isMobile ? "column" : "row",
            textAlign: isMobile ? "center" : "left"
          }}>
            <Avatar
              src={profilePhotoUrl}
              sx={{
                width: isMobile ? 60 : 80,
                height: isMobile ? 60 : 80,
                border: "3px solid rgba(255,255,255,0.8)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                cursor: "pointer"
              }}
              onClick={() => setIsUploadDialogOpen(true)}
            >
              {creatorName.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: 0.5 }}>
                {creatorName || "Your Creator Profile"}
              </Typography>
              <Typography variant="body2" sx={{
                opacity: 0.9, mb: 1,
                display: "flex", alignItems: "center", gap: 1,
                justifyContent: isMobile ? "center" : "flex-start"
              }}>
                <StarIcon fontSize="small" />
                {isVendor ? "Contractor/Vendor" : "Content Creator"}
                {creatorRegion && ` • ${creatorRegion}`}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
                <Chip
                  icon={<MonetizationOnIcon />}
                  label={`${platformSummary.activePlatforms} Platform${platformSummary.activePlatforms !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ backgroundColor: "rgba(76, 175, 80, 0.8)", color: "white", fontWeight: 600 }}
                />
                <Chip
                  icon={<AccountBalanceWalletIcon />}
                  label={`${wallets.length} Payment${wallets.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ backgroundColor: "rgba(33, 150, 243, 0.8)", color: "white", fontWeight: 600 }}
                />
                <LinearProgress
                  variant="determinate"
                  value={completionProgress}
                  sx={{
                    width: 100, height: 8, borderRadius: 4,
                    backgroundColor: "rgba(26, 25, 25, 0.3)",
                    "& .MuiLinearProgress-bar": { backgroundColor: "rgba(190, 57, 57, 0.8)" }
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: "0.75rem" }}>
                  {completionProgress}% Complete
                </Typography>
              </Stack>
            </Box>
            {!isMobile && (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setIsUploadDialogOpen(true)}
                  sx={{
                    color: "black",
                    borderColor: "rgba(190, 57, 57, 0.9)",
                    "&:hover": { borderColor: "rgba(190, 57, 57, 0.5)", backgroundColor: "rgba(255,255,255,0.4)" }
                  }}
                >
                  Change Photo
                </Button>
                <Button
                  variant="contained"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.1)" }
                  }}
                >
                  Logout
                </Button>
              </Stack>
            )}
          </Box>
          
          <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }}>
            {[
              {
                icon: <PersonIcon />,
                value: platformSummary.totalFollowers.toLocaleString(),
                label: "Total Followers",
              },
              {
                icon: <MonetizationOnIcon />,
                value: platformSummary.activePlatforms,
                label: "Active Platforms",
              },
              {
                icon: <AccountBalanceWalletIcon />,
                value: wallets.length,
                label: "Payment Methods",
              },
              {
                icon: <CheckCircleIcon />,
                value: taxInfo ? "Complete" : "Pending",
                label: "Tax Info",
              }
            ].map((metric, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper sx={{
                  p: isMobile ? 1 : 1.5,
                  textAlign: "center",
                  background: "rgba(255,255,255,0.5)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.4)",
                  color: "rgba(190, 57, 57, 0.9)"
                }}>
                  {React.cloneElement(metric.icon, { sx: { fontSize: isMobile ? 20 : 24, mb: 0.5, opacity: 0.8 } })}
                  <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 700, mb: 0.25 }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: "0.7rem" }}>
                    {metric.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          
          {!isMobile && (
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": { backgroundColor: "rgba(190, 57, 57, 0.9)", height: 3 },
                "& .MuiTab-root": {
                  color: "rgba(31, 30, 30, 0.9)",
                  fontWeight: 600,
                  "&.Mui-selected": { color: "rgba(190, 57, 57, 0.9)" }
                }
              }}
            >
              {tabItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Tab
                    key={item.value}
                    icon={
                      <Badge variant="dot" color="error" invisible={item.error === 0}>
                        <IconComponent />
                      </Badge>
                    }
                    label={item.label}
                    value={item.value}
                    iconPosition="start"
                  />
                );
              })}
            </Tabs>
          )}
        </Box>
      </Box>
      
      {isMobile && <MobileNavigation />}
      
      <Container maxWidth="lg" sx={{ pb: isMobile ? `${MOBILE_BOTTOM_SPACING}px` : 3, px: isMobile ? 1 : 3,  }} ref={contentRef}>
        {tabValue === "platforms" && (
          <Box sx={{ mb: 3,  }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <PlatformsTab
                tabValue={tabValue}
                index="platforms"
                showPlatformsTip={false}
                onSaveSuccess={(message) => showSnackbar(message)}
                onSaveError={(error) => showSnackbar(error, "error")}
                onDataUpdate={handlePlatformDataUpdate}
              />
            </CardContent>
          </Box>
        )}
        
        {tabValue === "partner" && (
          <Box sx={{ mb: 3,  }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <PartnerTab
                tabValue={tabValue}
                index="partner"
                processEmails={processEmails}
                setProcessEmails={setProcessEmails}
                forwardedAlias={forwardedAlias}
                setForwardedAlias={setForwardedAlias}
                refreshToken={refreshToken}
                setRefreshToken={setRefreshToken}
                onSave={handleSaveNonPlatformData}
              />
            </CardContent>
          </Box>
        )}
        
        {tabValue === "wallets" && (
          <Box sx={{ mb: 3,  }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack spacing={3}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                  <Typography variant="h5">Payment Methods</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenWalletDialog()}
                    sx={{ borderRadius: 2 }}
                    size={isMobile ? "small" : "medium"}
                  >
                    {isMobile ? "Add" : "Add Payment Method"}
                  </Button>
                </Box>
                
                {!walletsLoading && wallets.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Enable Automatic Payouts</AlertTitle>
                    <Typography variant="body2">
                      Add a payment method to receive automatic payouts when brands send you invoices.
                    </Typography>
                  </Alert>
                )}
                
                {walletsLoading ? (
                  <Stack spacing={2}>
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                    ))}
                  </Stack>
                ) : wallets.length === 0 ? (
                  <Card sx={{ textAlign: "center", py: 6, border: "2px dashed", borderColor: "divider" }}>
                    <CardContent>
                      <AccountBalanceWalletIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                      <Typography variant="h6" gutterBottom>No Payment Methods Added</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
                        Add a payment method to receive payments and manage your earnings.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenWalletDialog()}
                        size="large"
                        sx={{ borderRadius: 2 }}
                      >
                        Add Your First Payment Method
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Stack spacing={2}>
                    {wallets.map((wallet) => (
                      <WalletCard
                        key={wallet.id}
                        wallet={wallet}
                        onSetPrimary={handleSetPrimaryWallet}
                        onEdit={handleOpenWalletDialog}
                        onDelete={handleDeleteWallet}
                      />
                    ))}
                  </Stack>
                )}

                {/* Auto-Accept Payment Preferences */}
                <PaymentPreferencesCard
                  primaryWallet={wallets.find(w => w.is_primary)}
                />

                <Divider />

                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">Tax Information</Typography>
                    <Button
                      variant={taxInfo ? "outlined" : "contained"}
                      color={taxInfo ? "primary" : "warning"}
                      startIcon={taxInfo ? <EditIcon /> : <AddIcon />}
                      onClick={handleOpenTaxDialog}
                      sx={{ borderRadius: 2 }}
                      size={isMobile ? "small" : "medium"}
                    >
                      {isMobile ? (taxInfo ? "Update" : "Add") : (taxInfo ? "Update Tax Info" : "Add Tax Information")}
                    </Button>
                  </Box>
                  
                  {taxInfoLoading ? (
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                  ) : taxInfo ? (
                    <Card sx={{ border: 1, borderColor: "success.main", backgroundColor: "success.50" }}>
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                          <CheckCircleIcon color="success" />
                          <Typography variant="h6" color="success.main">Tax Information Complete</Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Legal Name</Typography>
                            <Typography variant="body1" fontWeight={500}>{taxInfo.legal_name}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Tax ID Type</Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {TAX_ID_TYPES.find(t => t.value === taxInfo.tax_id_type)?.label || taxInfo.tax_id_type}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Country</Typography>
                            <Typography variant="body1" fontWeight={500}>{taxInfo.country}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Status</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {taxInfo.w9_submitted && <Chip label="W9 Submitted" color="success" size="small" />}
                              {taxInfo.w8_submitted && <Chip label="W8 Submitted" color="success" size="small" />}
                              {!taxInfo.w9_submitted && !taxInfo.w8_submitted && (
                                <Chip label="Forms Pending" color="warning" size="small" />
                              )}
                            </Stack>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card sx={{ border: "2px dashed", borderColor: "warning.main", backgroundColor: "warning.50" }}>
                      <CardContent sx={{ textAlign: "center", py: 4 }}>
                        <WarningIcon sx={{ fontSize: 48, color: "warning.main", mb: 2 }} />
                        <Typography variant="h6" gutterBottom color="warning.main">Tax Information Required</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
                          Complete your tax information to ensure proper payment processing.
                        </Typography>
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={handleOpenTaxDialog}
                          startIcon={<AddIcon />}
                          sx={{ borderRadius: 2 }}
                        >
                          Add Tax Information
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Box>
        )}
        
        {tabValue === "appearance" && (
          <Box sx={{ mb: 3}}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}>
                Appearance Settings
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Customize your interface appearance, colors, and layout preferences.
              </Typography>
              
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth select
                    label="Theme Mode"
                    value={appearanceSettings.darkMode}
                    onChange={(e) =>
                      setAppearanceSettings((prev) => ({
                        ...prev,
                        darkMode: e.target.value === "" ? null : e.target.value === "true",
                      }))
                    }
                    size={isMobile ? "small" : "medium"}
                    helperText="Choose your preferred theme"
                  >
                    <MenuItem value="">System Default</MenuItem>
                    <MenuItem value="true">Dark Mode</MenuItem>
                    <MenuItem value="false">Light Mode</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth select
                    label="UI Density"
                    value={appearanceSettings.uiDensity}
                    onChange={(e) => setAppearanceSettings((prev) => ({ ...prev, uiDensity: e.target.value }))}
                    size={isMobile ? "small" : "medium"}
                    helperText="Adjust spacing"
                  >
                    <MenuItem value="compact">Compact</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="comfortable">Comfortable</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth select
                    label="Font Family"
                    value={appearanceSettings.fontPreference}
                    onChange={(e) => setAppearanceSettings((prev) => ({ ...prev, fontPreference: e.target.value }))}
                    size={isMobile ? "small" : "medium"}
                    helperText="Choose font style"
                  >
                    <MenuItem value="default">System Default</MenuItem>
                    <MenuItem value="roboto">Roboto</MenuItem>
                    <MenuItem value="open-sans">Open Sans</MenuItem>
                    <MenuItem value="montserrat">Montserrat</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: isMobile ? 2 : 3 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>Color Customization</Typography>
                  <Box sx={{
                    display: "flex", gap: 3,
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "center" : "flex-start"
                  }}>
                    {["primary", "secondary"].map((colorKey) => (
                      <Box key={colorKey} sx={{ textAlign: "center" }}>
                        <FormHelperText sx={{ mb: 1, fontSize: "0.875rem" }}>
                          {colorKey.charAt(0).toUpperCase() + colorKey.slice(1)} Color
                        </FormHelperText>
                        <Paper elevation={2} sx={{ p: 1, borderRadius: 2, display: "inline-block", border: 1, borderColor: "divider" }}>
                          <SketchPicker
                            color={appearanceSettings.themeColors[colorKey]}
                            onChangeComplete={(color) =>
                              setAppearanceSettings((prev) => ({
                                ...prev,
                                themeColors: { ...prev.themeColors, [colorKey]: color.hex }
                              }))
                            }
                            width={isMobile ? "250px" : undefined}
                          />
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: isMobile ? 2 : 3 }} />
                  <Box sx={{ display: "flex", justifyContent: isMobile ? "center" : "flex-end", gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setAppearanceSettings({
                          darkMode: null,
                          fontPreference: "default",
                          uiDensity: "normal",
                          themeColors: {
                            primary: theme.palette.mode === 'dark' ? "#cb534f" : "#7b2a2a",
                            secondary: "#646aff",
                          },
                        });
                      }}
                      size={isMobile ? "medium" : "large"}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveAppearance}
                      disabled={saveAppearanceSettingsMutation.isLoading}
                      size={isMobile ? "medium" : "large"}
                      sx={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)" }
                      }}
                    >
                      {saveAppearanceSettingsMutation.isLoading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Box>
        )}
        
        {tabValue === "notifications" && <NotifSettings />}
        
        {tabValue === "contracts" && (
          <Box sx={{ mb: 3,  }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <CreatorContractView />
            </CardContent>
          </Box>
        )}
        
        {tabValue === "accounts" && (
          <Box sx={{ mb: 3,  }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <AccountManagerTab />
            </CardContent>
          </Box>
        )}
      </Container>
      
      {isMobile && (
        <Paper sx={{
          position: "fixed",
          bottom: `${APPSHELL_BAR_HEIGHT}px`,
          left: 0, right: 0,
          zIndex: 999,
          borderTop: 1,
          borderColor: "divider"
        }} elevation={3}>
          <BottomNavigation value={tabValue} onChange={handleChangeTab} sx={{ height: EDITCREATOR_TAB_BAR_HEIGHT }}>
            {tabItems.slice(0, 4).map((tab) => {
              const IconComponent = tab.icon;
              return (
                <BottomNavigationAction
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  icon={
                    <Badge variant="dot" color="error" invisible={tab.error === 0}>
                      <IconComponent />
                    </Badge>
                  }
                />
              );
            })}
          </BottomNavigation>
        </Paper>
      )}
      
      {hasUnsavedChanges && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleSaveNonPlatformData}
          sx={{
            position: "fixed",
            bottom: isMobile ? `${MOBILE_BOTTOM_SPACING + 8}px` : 24,
            right: 16,
            zIndex: 1000,
            boxShadow: 6,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
            transition: "all 0.2s ease"
          }}
        >
          <SaveIcon sx={{ mr: 1 }} />
          Save Profile Changes
        </Fab>
      )}
      
      <WalletDialog
        open={walletDialog.open}
        mode={walletDialog.mode}
        wallet={walletDialog.wallet}
        walletData={newWallet}
        onClose={handleCloseWalletDialog}
        onSubmit={handleSubmitWallet}
        onChange={handleWalletChange}
        isMobile={isMobile}
        isCreator={true}
        existingPayPalWallet={wallets.find(w => w.wallet_type === 'paypal')}
        existingStripeWallet={wallets.find(w => w.wallet_type === 'stripe')}
        onLinkBankSuccess={(walletData) => {
          setSnackbar({ open: true, message: "Bank account linked successfully!", severity: "success" });
          fetchWallets();
          handleCloseWalletDialog();
        }}
        onLinkBankError={(err) => {
          setSnackbar({ open: true, message: "Failed to link bank account.", severity: "error" });
        }}
      />
      
      <TaxInfoDialog
        open={taxDialog}
        taxData={newTaxInfo}
        onClose={handleCloseTaxDialog}
        onSubmit={handleSaveTaxInfo}
        onChange={handleTaxChange}
        isMobile={isMobile}
      />
      
      <UploadFileDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onUploadSuccess={(data) => {
          if (data?.url) setProfilePhotoUrl(data.url);
          setIsUploadDialogOpen(false);
        }}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ mb: isMobile ? `${MOBILE_BOTTOM_SPACING + 8}px` : 0 }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}